<template>
  <div class="candidate-list-container" :style="isExpendFitScore && expendStyle">
    <el-table
      ref="candidateListTable"
      class="candidate-list"
      row-key="id"
      tooltip-effect="light"
      height="100%"
      :default-sort="{prop: 'apply_time', order: 'descending'}"
      :data="data"
      :empty-text="$t('el.table.emptyText')"
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
      @select-all="handleSelectAll"
    >
      <el-table-column width="35px" prop="id" type="selection" class-name="selection" :selectable="(row) => !row.is_agency_uploaded_and_hid"></el-table-column>
      <el-table-column min-width="150px" prop="candidate" :label="$t(`${i18nPrefix}.label.candidateName`)">
        <template #default="{ row }">
          <div
            class="avatar-name-container"
            :class="[row.reviewed || 'is-new', row.candidate.is_employee && 'employee', row.candidate.is_alumni && 'alumni']"
          >
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="row.candidate.candidateStatusName"
            >
              <div class="avatar-container">
                <img class="avatar" :src="getCandidateAvatarPath(row)" @click="handleAvatarClick(row)"/>
              </div>
            </el-tooltip>
            <div class="name-container">
              <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="row.candidate.name">
                <div class="name" @click="handleNameClick(row)">{{ row.candidate.name }}</div>
              </el-tooltip>
              <div class="candidate-icon-container">
                <el-tooltip
                  v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SHARED"
                  effect="light"
                  popper-class="light-tooltip-popper"
                  placement="bottom"
                  :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED ? 'employeeReferral' : 'candidateReferral'}`)"
                >
                  <span class="candidate-icon referral">
                    <i class="iconfont iconreferral"></i>
                  </span>
                </el-tooltip>
                <DuplicateCandidatePopover
                  v-if="hasDuplicateCandidatePermission && row.duplicate_candidate_list && row.duplicate_candidate_list.length > 0"
                  class="candidate-icon duplicate-candidate"
                  :duplicateCandidateList="row.duplicate_candidate_list"
                  @openDuplicateCandidateDetail="openDuplicateCandidateDetail"
                >
                  <DuplicateCandidateIcon :size="size"></DuplicateCandidateIcon>
                </DuplicateCandidatePopover>
                <el-tooltip
                  v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SUBMITTED"
                  effect="light"
                  popper-class="light-tooltip-popper"
                  placement="bottom"
                  :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED ? 'employeeSubmitted' : 'candidateSubmitted'}`)"
                >
                  <span  class="candidate-icon submit">
                    <i class="iconfont icona-bianzu53"></i>
                    <i class="iconfont iconxingzhuang dot" :style="dotStyle"></i>
                  </span>
                </el-tooltip>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column min-width="190px" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.workCompanyName`)">
        <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.company)|empty }}</template>
      </el-table-column>
      <el-table-column min-width="100px" prop="" label-class-name="work-experience-year" :label="$t(`${i18nPrefix}.label.workExperienceYear`)">
        <template #default="{ row }">{{ row.totalWorkYear|empty }}</template>
      </el-table-column>
      <el-table-column min-width="135px" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.lastWorkJobName`)">
        <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.job)|empty }}</template>
      </el-table-column>
      <el-table-column min-width="130px" prop="max_degree" show-overflow-tooltip sortable="custom" :sort-orders="['descending', 'ascending']"  :label="$t(`${i18nPrefix}.label.degree`)">
        <template #default="{ row }">{{ row.candidate.maxDegreeI18nName|empty }}</template>
      </el-table-column>
      <el-table-column min-width="75px" prop="candidate" label-class-name="age-gender-header" :label="$t(`${i18nPrefix}.label.ageAndGender`)">
        <template #default="{ row }">
          <span class="age">{{ row.candidate.age }}</span>
          <span v-if="row.candidate.gender === 'M' || row.candidate.gender === 'F'" class="gender">
            <i
              class="iconfont"
              :class="`iconResume-${row.candidate.gender === 'M' ? 'male' : 'female'}`"
            />
          </span>
        </template>
      </el-table-column>
      <el-table-column min-width="95px" show-overflow-tooltip prop="location" :label="$t(`${i18nPrefix}.label.location`)">
        <template #default="{ row }">{{ row.locationI18nName|empty }}</template>
      </el-table-column>
      <el-table-column min-width="105px" prop="apply_time" label-class-name="apply-time-header" sortable="custom" :sort-orders="['descending', 'ascending']" :label="$t(`${i18nPrefix}.label.appliedDate`)">
        <template #default="{ row }">{{ row.apply_time|formatDate }}</template>
      </el-table-column>
      <el-table-column min-width="85px" prop="statusName" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.status`)">
        <template #default="{ row }">
          <el-button class="candidate-status" type="text" :disabled="true">{{ formatAgencyUploadStatus(row.agency_upload_status) }}</el-button>
        </template>
      </el-table-column>
      <el-table-column width="45px" v-if="showAssessmentFit || showResumeFit">
        <template #default="{ row }">
          <div class="open-fit-btn" :class="row.candidate.hasFit && 'active'" @click="handleExpendFitScore">%</div>
        </template>
      </el-table-column>
      <el-table-column width="55px" prop="id">
        <template #default="{ row }">
          <el-dropdown v-if="!row.is_agency_uploaded_and_hid" class="operation-btn operation-btn-dropdown" trigger="click" @command="handleOperation($event, row)">
            <i class="btn-icon el-icon-more"></i>
            <template #dropdown>
              <el-dropdown-menu class="no-arrow-dropdown-menu">
                <template v-for="(item, index) in operationList">
                  <el-dropdown-item
                    v-if="showBtn(item, row)"
                    :key="index"
                    :command="item"
                  >
                    <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                    <el-popover
                      v-if="item.childrenMenuList"
                      placement="left"
                      trigger="hover"
                      popper-class="action-children-container"
                    >
                      <div
                        v-for="(child, childIndex) in item.childrenMenuList"
                        v-show="!child.hidden"
                        class="child-item"
                        :key="childIndex"
                        @click="handleOperation(child, row)"
                      >
                        {{ child.label }}
                      </div>
                      <div slot="reference">{{ item.label }}</div>
                    </el-popover>
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="data?.length" class="fit-score-container" :style="fitScoreContainerStyle">
      <el-table ref="fitScoreTable" class="fit-table" :data="[]" :default-sort="fitScoreDefaultSort" @sort-change="handleFitScoreTableSortChange">
        <el-table-column
          v-for="item in fitScoreHeaderList"
          sortable="custom"
          label-class-name="fit-score-label"
          :min-width="item.width"
          :key="item.label"
          :prop="item.orderField"
          :sort-orders="['descending', 'ascending']"
        >
          <template #header>
            {{ item.label }}
          </template>
        </el-table-column>
      </el-table>
      <div ref="fitScoreBody" class="fit-score-body">
        <template v-if="fitScoreRowHeight">
          <FitScoreRow
            v-for="(item) in data"
            :key="item.id"
            :fitScoreRowHeight="fitScoreRowHeight"
            :personalityFinished="item.candidate.personality_finished"
            :problemSolvingFinished="item.candidate.problem_solving_finished"
            :cultureFit="item.candidate.organization_fit"
            :personalityFit="item.candidate.job_fit"
            :problemSolvingFit="item.candidate.mental_ability"
            :resumeFitScore="item.ai_match_info?.resume_ai_match_score"
            :aiResumeMatchSummary="getResumeAIMatchSummary(item)"
            :showProblemSolvingFit="showProblemSolvingFit"
            :showAssessmentFit="showAssessmentFit"
          />
        </template>
      </div>
      <div class="btn-container" @click="handleExpendFitScore">
        <i class="close-fit-btn el-icon-caret-right"/>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { getI18nPropertyValue } from '@/i18n'
import { ASSESSMENT_INVITE_TYPE, REFERRAL_TYPE } from '@/constant/application'
import DuplicateCandidatePopover from '@/views/adminNew/application/components/duplicateCandidatePopover.vue'
import DuplicateCandidateIcon from '@/components/icon/duplicateCandidate.vue'
import FitScoreRow from '../../../adminNew/jobApplicantList/fitScoreRow.vue'

export default {
  components: {
    DuplicateCandidateIcon,
    DuplicateCandidatePopover,
    FitScoreRow
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    operationList: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    showAssessmentFit: {
      default: false
    },
    showProblemSolvingFit: {
      default: false
    },
    cancelSelectedCandidateList: {
      type: Array,
      required: true
    },
    showAIResumeMatch: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      moment,
      getI18nPropertyValue,
      REFERRAL_TYPE,
      atBottom: false,
      emitScrollBottom: null,
      isExpendFitScore: false,
      fitScoreRowHeight: null,
      dotColor: '#51bf82',
      size: 14,
      fitScoreDefaultSort: {
        prop: '',
        order: 'descending'
      },
      hasAddCandidateTableScrollListener: false,
      hasAddFitScrollListener: false,
      hasAddResizeListener: false
    }
  },
  computed: {
    fitScoreContainerRight () {
      return 105 * this.fitScoreHeaderList?.length
    },
    fitScoreContainerStyle () {
      return {
        right: this.isExpendFitScore ? '0px' : `-${this.fitScoreContainerRight + 16}px`,
        width: `${93 * this.fitScoreHeaderList?.length}px`
      }
    },
    expendStyle () {
      return {
        paddingRight: `${93 * this.fitScoreHeaderList?.length - 20}px`
      }
    },
    dotStyle () {
      const dotSize = this.dotSize ? this.dotSize : this.size * 0.6
      const style = { color: this.dotColor }
      if (dotSize >= 12) {
        style.fontSize = `${dotSize}px`
      } else {
        style.fontSize = '12px'
        style.transform = `scale(${dotSize / 12})`
      }
      return style
    },
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    },
    fitScoreHeaderList () {
      const i18nPrefix = this.i18nPrefix
      const list = [
        {
          hidden: !this.showAssessmentFit,
          class: 'culture-fit',
          label: this.$t(`${i18nPrefix}.label.cultureFit`),
          orderField: 'organization_fit',
          width: '60px'
        },
        {
          hidden: !this.showAssessmentFit,
          class: 'personality-fit',
          label: this.$t(`${i18nPrefix}.label.personalityFit`),
          orderField: 'job_fit',
          width: '68px'
        },
        {
          hidden: !this.showProblemSolvingFit,
          class: 'problem-solving-fit',
          label: this.$t(`${i18nPrefix}.label.problemSolvingFit`),
          orderField: 'mental_ability',
          width: '60px'
        },
        {
          hidden: !this.showAIResumeMatch,
          class: 'resume-fit',
          label: this.$t('admin.application.candidate.title.aiResumeMatchScore'),
          orderField: 'resume_ai_match_score',
          width: '60px'
        }
      ]
      return _.filter(list, item => !item.hidden)
    }
  },
  created () {
    this.emitScrollBottom = _.debounce(() => {
      this.$emit('scrollBottom')
    }, 200)
  },
  mounted () {
    if (this.data?.length) {
      this.addFitScoreScroll()
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.computeTableSize)
  },
  methods: {
    getResumeAIMatchSummary (application) {
      if (!application.ai_match_info) {
        return ''
      }
      return getI18nPropertyValue(application.ai_match_info, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    },
    addFitScoreScroll () {
      const fitScoreBody = this.$refs.fitScoreBody
      const tableBody = document.querySelector('.el-table__body-wrapper')

      if (!this.hasAddFitScrollListener) {
        fitScoreBody.addEventListener('scroll', () => {
          tableBody.scrollTop = fitScoreBody.scrollTop
        })
        this.hasAddFitScrollListener = true
      }

      if (!this.hasAddCandidateTableScrollListener) {
        tableBody.addEventListener('scroll', () => {
          fitScoreBody.scrollTop = tableBody.scrollTop
          const distance = tableBody.scrollHeight - tableBody.scrollTop - tableBody.clientHeight - 10
          if (distance <= 50) {
            if (!this.atBottom) {
              this.emitScrollBottom()
            }
            this.atBottom = true
          } else {
            this.atBottom = false
          }
        })
        this.hasAddCandidateTableScrollListener = true
      }

      if (!this.hasAddResizeListener) {
        window.addEventListener('resize', this.computeTableSize)
        this.hasAddResizeListener = true
      }
      this.$nextTick(() => {
        this.computeTableSize()
      })
    },
    refreshSelectAllCandidate () {
      const noSelectedList = _.concat([], this.cancelSelectedCandidateList, _.filter(this.data, item => item.is_agency_uploaded_and_hid))
      this.$refs.candidateListTable.store.states.selection = _.difference(this.data, noSelectedList)
    },
    formatAgencyUploadStatus (status) {
      if (status) {
        return this.$t(`${this.i18nPrefix}.agencyUploadStatus.${status}`)
      } else {
        return ''
      }
    },
    handleSortChange ({ prop, order }) {
      this.$refs.fitScoreTable.clearSort()
      this.fitScoreDefaultSort = { prop: '', order: '' }
      this.$emit('orderChange', prop, order === 'descending' ? '-' : '')
    },
    handleFitScoreTableSortChange ({ prop, order }) {
      this.$refs.candidateListTable.clearSort()
      this.$emit('orderChange', prop, order === 'descending' ? '-' : '')
      this.fitScoreDefaultSort.prop = prop
      this.fitScoreDefaultSort.order = order
    },
    handleSelectionChange (val) {
      this.$emit('selectedChange', val)
    },
    handleSelectAll (val) {
      this.$emit('selectAll', val)
    },
    handleOperation (operation, row) {
      operation.operator && operation.operator(row)
    },
    handleNameClick (row) {
      this.$emit('openDetail', row)
    },
    handleAvatarClick (row) {
      this.$emit('openDetail', row)
    },
    getCandidateAvatarPath (row) {
      return row.is_agency_uploaded_and_hid ? this.$DjangoStaticUrl('images_en/default.jpg') : this.$DjangoBaseUrl(row.candidate.photo)
    },
    scrollToTop () {
      const el = this.$el.querySelector('.el-table__body-wrapper')
      el.scrollTo({
        top: 0
      })
    },
    handleExpendFitScore () {
      this.isExpendFitScore = !this.isExpendFitScore
    },
    computeTableSize () {
      const tableEl = this.$refs.candidateListTable.$el
      const fitScoreTableEl = this.$refs.fitScoreTable.$el
      const tableRow = tableEl.querySelector('.el-table__row')
      this.fitScoreRowHeight = `${tableRow?.offsetHeight}px`

      const tableHeader = tableEl.querySelector('.el-table__header-wrapper')
      const fitScoreHeader = fitScoreTableEl.querySelector('.el-table__header-wrapper')
      tableHeader.querySelector('.el-table__cell').style.height = `${fitScoreHeader?.querySelector('.el-table__cell').offsetHeight}px`

      const tableBody = tableEl.querySelector('.el-table__body-wrapper')
      const fitScoreBody = this.$refs.fitScoreBody
      fitScoreBody.style.height = `${tableBody.clientHeight}px`
    },
    showBtn (operation, rowData) {
      if (operation.key) {
        if (ASSESSMENT_INVITE_TYPE.PERSONALITY === operation.key) {
          return !rowData.candidate.personality_finished
        }
        if (ASSESSMENT_INVITE_TYPE.PROBLEM_SOLVING === operation.key) {
          return !rowData.candidate.problem_solving_finished
        }
      }
      return true
    }
  },
  watch: {
    loading: {
      handler () {
        if (this.loading) {
          return
        }
        this.$nextTick(() => {
          const fitScoreBody = this.$refs.fitScoreBody
          const tableEl = this.$refs.candidateListTable.$el
          const tableBody = tableEl.querySelector('.el-table__body-wrapper')
          const tableRow = tableEl.querySelector('.el-table__row')
          this.fitScoreRowHeight = `${tableRow?.clientHeight}px`
          fitScoreBody.style.height = `${tableBody.clientHeight}px`
          tableBody.scrollTop = fitScoreBody.scrollTop
        })
      }
    },
    '$i18n.locale' () {
      this.$nextTick(() => {
        this.computeTableSize()
      })
    },
    data: {
      handler () {
        if (this.data?.length) {
          this.$nextTick(() => {
            this.addFitScoreScroll()
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-list-container {
  position: relative;
  height: 100%;
  display: flex;
  transition: .3s all;

  .candidate-list {
    .avatar-name-container {
      display: flex;
      align-items: center;

      &.is-new {
        .avatar-container {
          &::before {
            content: '';
            display: block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: #ee7815;
            position: absolute;
            top: 0;
            left: 0;
          }
        }
      }

      &.employee {
        .avatar-container {
          .avatar {
            border-color: #0096cc;
          }
        }
      }

      &.alumni {
        .avatar-container {
          .avatar {
            border-color: #ee7815;
          }
        }
      }

      .avatar-container {
        position: relative;
        flex-shrink: 0;

        .avatar-lock-icon {
          position: absolute;
          bottom: 2px;
          right: 6px;
          font-size: 14px;
          color: #9e9e9e;
        }

        .avatar {
          display: block;
          width: 44px;
          height: 44px;
          padding: 1px;
          border: 1px solid transparent;
          border-radius: 50%;
          line-height: 1;
          cursor: pointer;
        }
      }

      .name-container {
        flex: 1;
        margin-left: 22px;
        overflow: hidden;

        .name {
          font-size: 15px;
          white-space: nowrap;
          word-break: keep-all;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
        }

        .candidate-icon-container {
          line-height: 16px;

          .candidate-icon {
            cursor: pointer;

            & + .candidate-icon {
              margin-left: 5px;
            }

            &.submit, &.referral {
              .iconfont {
                font-size: 14px;
              }
            }

            &.submit {
              position: relative;

              .dot {
                border-radius: 50%;
                background: #fff;
                position: absolute;
                right: -3px;
                bottom: -3px;
                z-index: 1;
              }
            }
          }
        }
      }
    }

    .gender {
      margin-left: 2px;
    }

    .candidate-status {
      width: 100%;
      padding: 5px 0;
      border: 0;
      color: #0096cc;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .operation-btn {
      cursor: pointer;

      .btn-icon {
        transform: rotateZ(90deg);
      }
    }

    .open-fit-btn {
      width: 30px;
      height: 30px;
      line-height: 30px;
      background-color: #f3f3f3;
      text-align: center;
      color: #606266;
      border-radius: 50%;
      cursor: pointer;

      &.active {
        background: #0096cc;
        color: #fff;
      }

      &:hover {
        background: #008fc3;;
        color: #fff;
      }
    }

    ::v-deep {
      .cell {
        line-height: 24px;
      }

      .el-checkbox__inner {
        width: 18px;
        height: 18px;

        &::after {
          height: 9px;
          left: 6px;
          top: 2px;
        }
      }

      .selection {
        .cell {
          padding: 0;
          text-align: center;
        }
      }

      .is-checked,
      .is-indeterminate {
        .el-checkbox__inner {
          background-color: #0096cc;
          border-color: #0096cc;
        }
      }

      .work-experience-year,
      .age-gender-header,
      .apply-time-header {
        word-break: break-word;
      }

      .el-table__fixed-right {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.12);
      }

      .is-sortable.descending {
        .caret-wrapper {
          .sort-caret.descending {
            border-top-color: #0096cc;
          }
        }
      }

      .is-sortable.ascending {
        .caret-wrapper {
          .sort-caret.ascending {
            border-bottom-color: #0096cc;
          }
        }
      }
    }
  }

  .fit-score-container {
    height: 100%;
    background-color: #fff;
    box-shadow: 0 0 10px rgba(0, 0, 0, .12);
    transition: .3s all;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;

    .fit-table {
      ::v-deep .fit-score-label {
        word-break: break-word;
        text-align: center;
        font-size: 12px;
      }

      ::v-deep .el-table__body-wrapper {
        display: none;
      }

      ::v-deep {
        .is-sortable.descending {
          .caret-wrapper {
            .sort-caret.descending {
              border-top-color: #0096cc;
            }
          }
        }

        .is-sortable.ascending {
          .caret-wrapper {
            .sort-caret.ascending {
              border-bottom-color: #0096cc;
            }
          }
        }
      }
    }

    .fit-score-body {
      overflow: auto;
    }

    .btn-container {
      position: absolute;
      top: 125px;
      left: -16px;
      width: 16px;
      height: 35px;
      line-height: 35px;
      background: #fff;
      cursor: pointer;
      font-size: 12px;
      box-shadow: -2px 0px 10px rgba(0, 0, 0, .12);

      .close-fit-btn {
        margin-left: 2px;
      }
    }
  }
}

.action-children-container {
  padding: 10px 0;

  .child-item {
    line-height: 36px;
    padding: 0 20px;
    margin: 0;
    cursor: pointer;

    &:hover {
      color: #0096cc;
      background-color: #ecf5ff;
    }
  }
}
</style>
